import { Box, useTheme } from "@mui/material";
import { Button } from "components";
import { Strip } from "components/strip";
import type { ReactNode } from "react";
import type { WindowWithIntercom } from "../../types";
import { SUBSCRIPTION_BANNER_COPY } from "./pricingContent";
import { PRICING_DESIGN_VALUES } from "./pricingDesignValues";

// Same behaviour as before the redesign: opens the Intercom Messenger (Home).
const openIntercom = () => {
  (window as WindowWithIntercom).Intercom?.("show");
};

/**
 * Warning strip above the pricing page, shared by the cancelled and expired
 * subscription banners so a design change applies to both.
 */
export const SubscriptionStatusBanner = ({ label }: { label: ReactNode }) => {
  const theme = useTheme();
  const { yellow } = theme.palette.primitive.system;

  return (
    <Box role="status">
      <Strip
        variant="warning"
        closeable={false}
        // Icon colour; the text colour comes from sx.color (Strip's label inherits it).
        leadIconsx={{ color: yellow[800] }}
        sx={{
          backgroundColor: theme.palette["warning-minor"].tint,
          color: yellow[900],
          p: "12px 16px",
          minHeight: "44px",
          gap: "4px",
          position: "relative",
          // Bottom border drawn inset, so the banner stays 44px.
          boxShadow: `inset 0 -1px 0 ${yellow[400]}`,
          // Top border: inside the app, the fixed app bar (64px + 1px border)
          // overlaps the page's first pixel row, which hid this line under its
          // grey border. Lifting just this 1px line above the app bar matches
          // the design; it scrolls out of view as soon as the page scrolls.
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            backgroundColor: yellow[400],
            zIndex: theme.zIndex.appBar + 1,
            pointerEvents: "none",
          },
        }}
        label={label}
        rightAction={
          <Button
            variant="ghost"
            isLink
            onClick={openIntercom}
            sx={PRICING_DESIGN_VALUES.bannerLinkButton}
          >
            {SUBSCRIPTION_BANNER_COPY.contactSales}
          </Button>
        }
      />
    </Box>
  );
};
